<template>
    <div class="onboarding-tour" v-if="showTour">
        <!-- Welcome Modal -->
        <welcome-tour-modal 
            v-model="showWelcomeModal"
            :user-name="userName"
            @start-tour="startTour"
            @skip-tour="skipTour"
        />

        <!-- Tour Overlay -->
        <div v-if="tourStarted" class="tour-overlay" :class="{ 'tour-overlay--mobile': isMobile }" @click.self="handleOverlayClick">
            <!-- Highlight Box -->
            <div 
                v-if="currentHighlight"
                class="tour-highlight"
                :class="{ 'tour-highlight--mobile': isMobile }"
                :style="highlightStyle"
            ></div>

            <!-- Tooltip -->
            <div 
                v-if="currentStep"
                ref="tooltip"
                class="tour-tooltip"
                :class="[`tooltip-${tooltipPosition}`, { 'tour-tooltip--mobile': isMobile }]"
                :style="tooltipStyle"
            >
                <div class="tooltip-arrow" v-if="!isMobile" :class="[`arrow-${tooltipPosition}`]"></div>
                
                <div class="tooltip-header">
                    <span class="step-indicator">{{ currentStepIndex + 1 }} / {{ activeSteps.length }}</span>
                    <button class="close-btn" @click="endTour">
                        <v-icon size="18" color="grey">mdi-close</v-icon>
                    </button>
                </div>

                <div class="tooltip-content">
                    <h4 class="tooltip-title">{{ currentStep.title }}</h4>
                    <p class="tooltip-description">{{ currentStep.description }}</p>
                </div>

                <div class="tooltip-actions">
                    <v-btn 
                        v-if="currentStepIndex > 0"
                        text 
                        small 
                        color="grey darken-1"
                        @click="prevStep"
                    >
                        Back
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn 
                        v-if="currentStepIndex < activeSteps.length - 1"
                        color="primary" 
                        depressed 
                        small
                        @click="nextStep"
                    >
                        Next
                        <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                    <v-btn 
                        v-else
                        color="primary" 
                        depressed 
                        small
                        @click="finishTour"
                    >
                        Finish
                        <v-icon right small>mdi-check</v-icon>
                    </v-btn>
                </div>

                <!-- Progress dots -->
                <div class="tooltip-progress">
                    <span 
                        v-for="(step, index) in activeSteps" 
                        :key="index"
                        class="progress-dot"
                        :class="{ 'active': index === currentStepIndex, 'completed': index < currentStepIndex }"
                    ></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import WelcomeTourModal from './welcome-tour-modal.vue';
import GlobalComputed from '@/helpers/global-computed';

export default {
    name: 'OnboardingTour',
    components: {
        WelcomeTourModal
    },
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            showWelcomeModal: false,
            tourStarted: false,
            currentStepIndex: 0,
            currentHighlight: null,
            highlightStyle: {},
            tooltipStyle: {},
            tooltipPosition: 'bottom',

            // Desktop steps
            desktopSteps: [
                {
                    target: '[data-tour="side-menu"]',
                    title: 'Side Menu',
                    description: 'This is your main navigation. Access all features of Riwaq Al-Quran from here including your courses, calendar, wallet, and more.',
                    position: 'right',
                    padding: 0,
                },
                {
                    target: '[data-tour="courses-menu"]',
                    title: 'Course Catalog',
                    description: 'Browse our comprehensive collection of Quran courses. Find the perfect course for your learning journey.',
                    position: 'right',
                    padding: 4,
                    requiresMenuOpen: true
                },
                {
                    target: '[data-tour="search-filter"]',
                    title: 'Search & Filter',
                    description: 'Use the search and filter options to quickly find courses that match your interests and schedule.',
                    position: 'bottom',
                    padding: 8,
                    route: 'courses',
                    scrollTo: 'top'
                },
                {
                    target: '[data-tour="advisor-button"]',
                    title: 'Talk to an Advisor',
                    description: 'Need personalized guidance? Click here to connect with one of our education advisors who can help you choose the right course.',
                    position: 'left',
                    padding: 8,
                },
                {
                    target: '.whatsapp-button',
                    title: 'Need Help?',
                    description: 'Click here anytime to chat with our support team. We\'re here to help you succeed!',
                    position: 'top-right',
                    padding: 8,
                    scrollTo: 'bottom'
                },
                {
                    target: '[data-tour="profile-menu"]',
                    title: 'Profile & Settings',
                    description: 'Access your profile, settings, and manage your account from this menu.',
                    position: 'bottom-right',
                    padding: 4,
                    scrollTo: 'top',
                }
            ],

            // Mobile steps — only targets that exist on mobile
            mobileSteps: [
                {
                    target: '.v-bottom-navigation',
                    title: 'Navigation Bar',
                    description: 'Quickly access Home, My Courses, Calendar, Wallet, Inbox and Help from the navigation bar.',
                    position: 'top',
                    padding: 4,
                },
                {
                    target: '[data-tour="search-filter"]',
                    title: 'Search & Filter',
                    description: 'Find courses that match your interests using search and filter options.',
                    position: 'bottom',
                    padding: 8,
                    route: 'courses',
                    scrollTo: 'top',
                },
                {
                    target: '[data-tour="advisor-button"]',
                    title: 'Talk to an Advisor',
                    description: 'Need guidance? Tap here to connect with one of our education advisors.',
                    position: 'left',
                    padding: 8,
                },
                {
                    target: '.whatsapp-button',
                    title: 'WhatsApp Support',
                    description: 'Tap here anytime to chat with our support team on WhatsApp.',
                    position: 'left',
                    padding: 8,
                },
            ]
        };
    },
    computed: {
        appData: GlobalComputed.appData,
        showTour() {
            if (!this.user) return false;
            if (this.user.tour_taken) return false;
            if (!this.user.email_verified_at) return false;
            if (this.user.interests_completed != 1) return false;
            // Don't run the tour until the student has finished booking
            // their initial session — same gate as the policy dialog.
            if (this.user.needs_evaluation_redirect) return false;
            if (this.user.needs_consultation_redirect) return false;
            // Wait for the Student Classes Policy to be acknowledged before
            // firing the tour, so the order is: book → policy → tour.
            if (this.policyPending) return false;
            const r = this.$route.name;
            return r !== 'interests' && r !== 'evaluation-booking' && r !== 'consultation-booking';
        },
        policyPending() {
            const publishedAt = this.appData ? this.appData.student_classes_policy_published_at : null;
            if (!publishedAt) return false;
            const policyHtml = this.appData ? (this.appData.student_classes_policy || '') : '';
            const stripped = policyHtml ? String(policyHtml).replace(/<[^>]*>/g, '').trim() : '';
            if (!stripped) return false;
            const ack = this.user ? this.user.student_policy_acknowledged_at : null;
            if (!ack) return true;
            return new Date(ack).getTime() < new Date(publishedAt).getTime();
        },
        userName() {
            if (!this.user || !this.user.name) return '';
            return this.user.name.split(' ')[0];
        },
        currentStep() {
            return this.activeSteps[this.currentStepIndex] || null;
        },
        isMobile() {
            return window.innerWidth < 960;
        },
        activeSteps() {
            return this.isMobile ? this.mobileSteps : this.desktopSteps;
        }
    },
    watch: {
        showTour: {
            immediate: true,
            handler(show) {
                if (show) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.showWelcomeModal = true;
                        }, 500);
                    });
                }
            }
        },
        currentStepIndex() {
            this.updateHighlight(true);
        },
        '$route'() {
            if (this.tourStarted) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.updateHighlight(true);
                    }, 300);
                });
            }
        }
    },
    methods: {
        startTour() {
            this.showWelcomeModal = false;
            this.tourStarted = true;
            this.currentStepIndex = 0;
            
            if (this.$route.name !== 'home') {
                this.$router.push({ name: 'home' });
            }
            
            this.$nextTick(() => {
                if (!this.isMobile) {
                    this.openSideMenuOnMobile();
                }
                setTimeout(() => {
                    this.updateHighlight(true);
                }, 300);
            });
        },
        openSideMenuOnMobile() {
            const isMobile = window.innerWidth < 960;
            if (isMobile) {
                let appComponent = this.$parent;
                while (appComponent && appComponent.$options.name !== 'App') {
                    appComponent = appComponent.$parent;
                }
                if (appComponent && appComponent.appMenu !== undefined) {
                    appComponent.appMenu = true;
                }
            }
        },
        ensureMenuOpen() {
            const isMobile = window.innerWidth < 960;
            if (isMobile) {
                let appComponent = this.$parent;
                while (appComponent && appComponent.$options.name !== 'App') {
                    appComponent = appComponent.$parent;
                }
                if (appComponent && appComponent.appMenu !== undefined) {
                    appComponent.appMenu = true;
                }
            }
        },
        skipTour() {
            this.showWelcomeModal = false;
            this.completeTourOnServer();
        },
        endTour() {
            this.tourStarted = false;
            this.currentHighlight = null;
            this.completeTourOnServer();
        },
        finishTour() {
            this.tourStarted = false;
            this.currentHighlight = null;
            this.completeTourOnServer();
            this.$emit('tour-completed');
        },
        async completeTourOnServer() {
            try {
                await this.$store.dispatch('completeTour');
            } catch (error) {
                console.error('Failed to save tour completion:', error);
            }
        },
        async nextStep() {
            const nextIndex = this.currentStepIndex + 1;
            if (nextIndex >= this.activeSteps.length) return;
            const nextStep = this.activeSteps[nextIndex];
            
            if (nextStep && nextStep.requiresMenuOpen) {
                this.ensureMenuOpen();
                await new Promise(resolve => setTimeout(resolve, 400));
            }
            
            if (nextStep && nextStep.route && this.$route.name !== nextStep.route) {
                this.$router.push({ name: nextStep.route });
                setTimeout(() => { this.currentStepIndex = nextIndex; }, 400);
            } else {
                if (nextStep && nextStep.scrollTo) {
                    await this.scrollToPosition(nextStep.scrollTo);
                }
                this.currentStepIndex = nextIndex;
            }
        },
        async prevStep() {
            const prevIndex = this.currentStepIndex - 1;
            if (prevIndex < 0) return;
            const prevStep = this.activeSteps[prevIndex];
            
            if (prevStep && this.currentStep.route && !prevStep.route) {
                this.$router.push({ name: 'home' });
                setTimeout(() => { this.currentStepIndex = prevIndex; }, 400);
            } else {
                if (prevStep && prevStep.scrollTo) {
                    await this.scrollToPosition(prevStep.scrollTo);
                }
                this.currentStepIndex = prevIndex;
            }
        },
        scrollToPosition(position) {
            return new Promise((resolve) => {
                if (position === 'bottom') {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                } else if (position === 'top') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setTimeout(resolve, 500);
            });
        },
        async updateHighlight(shouldScroll = true, retryCount = 0) {
            const step = this.currentStep;
            if (!step) return;

            if (shouldScroll && step.scrollTo) {
                await this.scrollToPosition(step.scrollTo);
            }

            const element = document.querySelector(step.target);
            
            if (element) {
                const rect = element.getBoundingClientRect();
                const padding = step.padding || 8;
                
                this.currentHighlight = rect;
                this.highlightStyle = {
                    top: `${rect.top - padding}px`,
                    left: `${rect.left - padding}px`,
                    width: `${rect.width + padding * 2}px`,
                    height: `${rect.height + padding * 2}px`
                };

                this.$nextTick(() => {
                    this.calculateTooltipPosition(rect, step.position);
                });
            } else {
                if (retryCount < 5) {
                    setTimeout(() => {
                        this.updateHighlight(shouldScroll, retryCount + 1);
                    }, 200);
                } else if (this.currentStepIndex < this.activeSteps.length - 1) {
                    // Element genuinely missing, skip to next step
                    this.currentStepIndex++;
                }
            }
        },
        calculateTooltipPosition(targetRect, preferredPosition) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const margin = 16;
            const tooltipWidth = this.isMobile ? Math.min(viewportWidth - 32, 340) : 320;
            
            // Use actual DOM height if available, otherwise fallback to 200
            let tooltipHeight = 200;
            if (this.$refs.tooltip) {
                tooltipHeight = this.$refs.tooltip.offsetHeight;
            }
            
            let top, left;

            if (this.isMobile) {
                // Mobile: always center horizontally, place above or below target
                left = (viewportWidth - tooltipWidth) / 2;
                
                const spaceAbove = targetRect.top;
                const spaceBelow = viewportHeight - targetRect.bottom;
                
                // If the target is very close to the bottom (like the bottom nav bar), 
                // FORCE it to be placed above to avoid overlapping.
                if (viewportHeight - targetRect.top < 120 || spaceAbove >= tooltipHeight + margin) {
                    // Place above
                    top = Math.max(margin, targetRect.top - tooltipHeight - margin);
                    this.tooltipPosition = 'top';
                } else if (spaceBelow >= tooltipHeight + margin) {
                    // Place below
                    top = targetRect.bottom + margin;
                    this.tooltipPosition = 'bottom';
                } else {
                    // Not enough space above or below, place in center of available space
                    if (spaceAbove > spaceBelow) {
                        top = Math.max(margin, targetRect.top - tooltipHeight - margin);
                        this.tooltipPosition = 'top';
                    } else {
                        top = targetRect.bottom + margin;
                        this.tooltipPosition = 'bottom';
                    }
                }
            } else {
                // Desktop positioning
                this.tooltipPosition = preferredPosition || 'bottom';

                switch (this.tooltipPosition) {
                    case 'bottom':
                        top = targetRect.bottom + margin;
                        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                        break;
                    case 'top':
                        top = targetRect.top - tooltipHeight - margin;
                        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                        break;
                    case 'right':
                        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
                        left = targetRect.right + margin;
                        break;
                    case 'left':
                        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
                        left = targetRect.left - tooltipWidth - margin;
                        break;
                    case 'bottom-right':
                        top = targetRect.bottom + margin;
                        left = targetRect.right - tooltipWidth;
                        break;
                    case 'top-left':
                        top = targetRect.top - tooltipHeight - margin;
                        left = targetRect.left;
                        break;
                    case 'top-right':
                        top = targetRect.top - tooltipHeight - margin;
                        left = targetRect.right - tooltipWidth;
                        break;
                    default:
                        top = targetRect.bottom + margin;
                        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                }
            }

            // Clamp to viewport
            if (left < margin) left = margin;
            if (left + tooltipWidth > viewportWidth - margin) left = viewportWidth - tooltipWidth - margin;
            if (top < margin) top = margin;
            if (top + tooltipHeight > viewportHeight - margin) top = viewportHeight - tooltipHeight - margin;

            this.tooltipStyle = {
                top: `${top}px`,
                left: `${left}px`,
                width: `${tooltipWidth}px`
            };
        },
        handleOverlayClick() {
            // Require button clicks
        },
        handleResize() {
            if (this.tourStarted) {
                this.updateHighlight(false);
            }
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    }
};
</script>

<style lang="scss" scoped>
.onboarding-tour {
    position: relative;
    z-index: 9999;
}

// ============================
// Overlay
// ============================
.tour-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 9998;

    // Lighter overlay on mobile so content is more visible
    &.tour-overlay--mobile {
        background: rgba(0, 0, 0, 0.4);
    }
}

// ============================
// Highlight
// ============================
.tour-highlight {
    position: fixed;
    background: transparent;
    border-radius: 8px;
    box-shadow: 
        0 0 0 4px rgba(231, 144, 7, 0.5),
        0 0 0 9999px rgba(0, 0, 0, 0.6);
    z-index: 9999;
    pointer-events: none;
    transition: all 0.3s ease;

    // Match the lighter overlay on mobile
    &.tour-highlight--mobile {
        box-shadow: 
            0 0 0 3px rgba(231, 144, 7, 0.6),
            0 0 0 9999px rgba(0, 0, 0, 0.4);
        border-radius: 12px;
    }
}

// ============================
// Tooltip
// ============================
.tour-tooltip {
    position: fixed;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    z-index: 10000;
    padding: 20px;
    animation: tooltipFadeIn 0.3s ease;

    // Mobile: bigger padding, rounder card, no arrow
    &.tour-tooltip--mobile {
        border-radius: 20px;
        padding: 24px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
    }
}

@keyframes tooltipFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

// ============================
// Tooltip Arrow (desktop only)
// ============================
.tooltip-arrow {
    position: absolute;
    width: 0;
    height: 0;
    border: 10px solid transparent;

    &.arrow-bottom { top: -20px; left: 50%; transform: translateX(-50%); border-bottom-color: white; }
    &.arrow-top { bottom: -20px; left: 50%; transform: translateX(-50%); border-top-color: white; }
    &.arrow-right { left: -20px; top: 50%; transform: translateY(-50%); border-right-color: white; }
    &.arrow-left { right: -20px; top: 50%; transform: translateY(-50%); border-left-color: white; }
    &.arrow-bottom-right { top: -20px; right: 24px; border-bottom-color: white; }
    &.arrow-top-left { bottom: -20px; left: 24px; border-top-color: white; }
    &.arrow-top-right { bottom: -20px; right: 24px; border-top-color: white; }
}

// ============================
// Header
// ============================
.tooltip-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.step-indicator {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #E79007;
    background: rgba(231, 144, 7, 0.1);
    padding: 4px 10px;
    border-radius: 12px;
}

.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.2s;
    &:hover { background: rgba(0, 0, 0, 0.05); }
}

// ============================
// Content
// ============================
.tooltip-content {
    margin-bottom: 16px;
}

.tooltip-title {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 16px;
    color: #1E1E1E;
    margin-bottom: 8px;
}

.tooltip-description {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: rgba(30, 30, 30, 0.7);
    line-height: 1.5;
    margin: 0;
}

// ============================
// Actions
// ============================
.tooltip-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

// ============================
// Progress
// ============================
.tooltip-progress {
    display: flex;
    justify-content: center;
    gap: 6px;
}

.progress-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &.active { background: #E79007; transform: scale(1.2); }
    &.completed { background: #994C26; }
}

// ============================
// Mobile enhancements
// ============================
@media (max-width: 960px) {
    .tooltip-title {
        font-size: 18px;
        margin-bottom: 10px;
    }

    .tooltip-description {
        font-size: 15px;
        line-height: 1.6;
    }

    .tooltip-actions {
        margin-bottom: 16px;
    }
}
</style>
